import React from 'react';
import { IconArrowRight, IconInfo, IconMic, IconStar } from '../icons';
import { fmtDateLong, ordinal, plural } from './util';
import { partBadge } from '../showPart';
import { FromTapeBadge, isFromTape, noVenue } from '../fromTape';
import './search.css';

const MATCH_MAX = 6;

const TAPE_BADGE = {
  sbd: { text: 'SBD', title: 'Soundboard recording' },
  mtx: { text: 'MTX', title: 'Matrix: soundboard mixed with audience' },
};

// "Set 2, 4th"; encores read "Encore"; tape-title matches have no position.
const where = (m) => {
  const set = typeof m.setLabel === 'string' ? m.setLabel.trim() : '';
  if (!set) return '';
  if (/^encore/i.test(set) && (!m.posInSet || m.posInSet === 1)) return set;
  return m.posInSet ? set + ', ' + ordinal(m.posInSet) : set;
};

const longTitle = (p) => (p >= 90 ? 'Longer than 9 in 10 versions' : 'Longer than 3 in 4 versions');

// The next matched song follows this one directly, and this one went into it.
const intoNext = (m, n) =>
  !!(m && n && m.into && n.setLabel === m.setLabel && Number.isFinite(m.posInSet) && n.posInSet === m.posInSet + 1);

// Shows matched from tape titles (fromTape) have no set, position or
// segue arrows: the tape's track order is not a setlist.
const Matched = ({ matched, fromTape }) => {
  const list = matched.slice(0, MATCH_MAX);
  const more = matched.length - list.length;
  return (
    <div className="ss-matched">
      {list.map((m, i) => {
        const pos = fromTape ? '' : where(m);
        const long = Number(m.lenPct) >= 75 && m.length;
        return (
          <React.Fragment key={m.key + ':' + i}>
            <span className="ss-m" title={m.name + (pos ? ' (' + pos + ')' : '') + (long ? ', ' + m.length + ', ' + longTitle(m.lenPct).toLowerCase() : '')}>
              <span className="ss-m-name">{m.name}</span>
              {pos && <span className="ss-m-pos">{pos}</span>}
              {long && <span className="ss-m-len mono">{m.length}</span>}
            </span>
            {!fromTape && intoNext(m, list[i + 1]) && (
              <span className="ss-m-into" title="Played into the next song">
                <IconArrowRight size={12} />
                <span className="ss-sr">into</span>
              </span>
            )}
          </React.Fragment>
        );
      })}
      {more > 0 && <span className="ss-m ss-m-more">{more} more</span>}
    </div>
  );
};

// One Set Search result in the Shows column. Rows are keyed and selected by
// `row.sid` (early and late shows share a date). onSelect(row).
// Optional (additive): `part` = { text, title } from showPart.partBadges for
// the whole list (it adds the "(1 of 2)" fallback); else row.part is used.
// `fillBand` (isFillBand(meta)): the band mixes setlist shows with shows
// matched from tape titles, so those rows carry a From tape badge.
export default function FindResultRow({ row, selected, onSelect, part, fillBand }) {
  if (!row) return null;
  const pb = part !== undefined ? part : partBadge(row);
  const tape = row.bestTape || null;
  const badge = tape && TAPE_BADGE[tape.sbd];
  const matched = Array.isArray(row.matched) ? row.matched : [];
  const of = Number(row.of) || 0;
  const hits = Number(row.hits) || 0;
  const fromTape = isFromTape(row);
  const venueUnknown = fromTape && noVenue(row.venue);
  return (
    <button
      type="button"
      className={'row ss-row' + (selected ? ' selected' : '')}
      aria-pressed={!!selected}
      onClick={(e) => {
        // a mouse click hands focus back to the page (Space keeps pausing)
        if (e.detail > 0 && e.currentTarget.blur) e.currentTarget.blur();
        if (onSelect) onSelect(row);
      }}
    >
      <div className="row-main">
        <div className="ss-row-top">
          <span className="row-title">{fmtDateLong(row.iso || row.displayDate || row.dateKey)}</span>
          {pb && (
            <span className="badge ss-part" title={pb.title}>
              {pb.text}
            </span>
          )}
          {fillBand && fromTape && <FromTapeBadge row={row} className="ss-fromtape" />}
          {badge && (
            <span className="sbd-badge" title={badge.title}>
              <IconMic size={11} /> {badge.text}
            </span>
          )}
          {tape && tape.rating10 != null && (
            <span className="rating ss-rating" title={tape.numReviews ? plural(tape.numReviews, 'review') : 'Rating'}>
              <IconStar size={11} /> {tape.rating10}
              <span className="dim">/10</span>
            </span>
          )}
        </div>
        {venueUnknown ? <div className="row-sub dim">Venue unknown</div> : <div className="row-sub">{row.venue}</div>}
        {!(venueUnknown && noVenue(row.location)) && <div className="row-sub dim">{row.location}</div>}
        {of > 0 && (
          <div className={'ss-hits' + (hits >= of ? ' all' : '')}>
            {hits} of {plural(of, 'song')}
          </div>
        )}
        {matched.length > 0 && <Matched matched={matched} fromTape={!!fillBand && fromTape} />}
        {row.explain && (
          <div className="ss-explain">
            <IconInfo size={12} />
            <span>{row.explain}</span>
          </div>
        )}
      </div>
      {row.length && <span className="badge mono">{row.length}</span>}
    </button>
  );
}
